import React, { useState, useEffect, useRef } from 'react';
import './PurchaseOrderDetailPage.css';
import { useSelector, useDispatch } from "react-redux";
import MenuBar from "../../components/MenuBar";
import Header from "../../components/Header";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from '../../components/placeholders/LoadingSpinner';
import httpClient from '../../components/helpers/httpClient';
import Label from '../../components/Label'
import { toast } from "react-toastify";
import { purchaseOrderStatusOptions, purchaseOrderStatusLabelColors } from '../../assets/data/data'

// Import components from purchase-section that will be reused or adapted
import StandardProductOrderLine from './StandardProductOrderLine';
import FreeLineOrderLine from './FreeLineOrderLine';
import TemplateModal from './TemplateModal';
import PopupDropdown from '../../components/PopupDropdown';
import PopupInput from '../../components/PopupInput';
import { showNotificationPopup } from '../../components/NotificationPopup';

// Import Icons (example, will add more as needed)
import { MdSave, MdPrint, MdEmail, MdExpand, MdDelete, MdEdit, MdAttachMoney, MdOutlineCloudDownload, MdInfo } from "react-icons/md";
import { GrTemplate } from "react-icons/gr";
import { AiOutlineFilePdf } from "react-icons/ai";
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { IoAddCircleOutline } from 'react-icons/io5';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { FaPhoneAlt, FaFileExcel } from "react-icons/fa";
import { CircularProgress } from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { IoCopySharp } from "react-icons/io5";
import { formatDate, parseDateValue, serializeDate } from '../../components/helpers/formatDate'

function PurchaseOrderDetailPage() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [unit, setUnit] = useState(null); // To store unit details
  const [project, setProject] = useState(null); // To store project details
  const [purchaseOrderLines, setPurchaseOrderLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Loading states for specific sections
  const [isLoadingOrderLines, setIsLoadingOrderLines] = useState(true);
  const [isLoadingPocs, setIsLoadingPocs] = useState(true);

  // States for POC section
  const [pocs, setPocs] = useState([]);

  // State for controlling detail info visibility
  const [showDetailInfo, setShowDetailInfo] = useState(false);

  // States migrated from PurchaseSection.jsx (will be refined)
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [contacts, setContacts] = useState([]); // For POCs or other contact related features
  const [companies, setCompanies] = useState([]);
  const [invoiceType, setInvoiceType] = useState('buyer_choices');
  const [approvalDate, setApprovalDate] = useState(null);
  const [invoiceManualOverride, setInvoiceManualOverride] = useState(false);
  const [invoicedTo, setInvoicedTo] = useState([]);
  const [defaultInvoicedTo, setDefaultInvoicedTo] = useState([]);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateMode, setTemplateMode] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [showingAlternativeButtons, setShowingAlternativeButtons] = useState(null);
  const [freeLineValues, setFreeLineValues] = useState({
    free_product_name: '',
    free_product_brand: '',
    free_product_description: '',
    free_price: ''
  });
  const [isPoEditable, setIsPoEditable] = useState(false);

  // VAT and Percentage Conversion functions (from PurchaseSection)
  const convertVatToPercentage = (value) => {
    if (value === null || value === undefined) return '';
    return (value * 100).toFixed(2);
  };

  const convertPercentageToVat = (value) => {
    if (value === null || value === undefined || value === '') return 0; // Handle empty string
    return parseFloat(value) / 100;
  };

  const convertDecimalToPercentage = (value) => {
    if (value === null || value === undefined) return '';
    return (value * 100).toFixed(2);
  };

  const convertPercentageToDecimal = (value) => {
    if (value === null || value === undefined || value === '') return 0; // Handle empty string
    return parseFloat(value) / 100;
  };

  const toggleCategoryCollapse = (categoryIndex) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  const handleIndividualProductChange = (categoryIndex, orderLineIndex, field, value, productIndex = null) => {
    if (!isPoEditable) return;
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines)); // Deep clone
    let targetLine;

    if (productIndex !== null) {
      // Product within a group (if group functionality is fully migrated)
      // For now, assuming direct order lines or simplified group structure
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex]?.products[productIndex];
    } else {
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex];
    }

    if (targetLine) {
      targetLine[field] = value;
      // Recalculate quantity_total if quantity or quantity_margin changes
      if (field === 'quantity' || field === 'quantity_margin') {
        const quantity = parseFloat(targetLine.quantity) || 0;
        const margin = parseFloat(targetLine.quantity_margin) || 0;
        targetLine.quantity_total = quantity * (1 + margin / 100);
      }
      // If changing price for a free line
      if ((targetLine.is_free_line || targetLine.isFreeType) && field === 'free_price') {
        targetLine.free_price = parseFloat(value) || 0;
      }
    }
    setPurchaseOrderLines(updatedLines);
  };

  const handleGroupInputChange = (categoryIndex, groupLineIndex, field, value, propagateToProducts = true) => {
    if (!isPoEditable) return;
    // This function might need significant adaptation if deep group editing is required.
    // For now, let's assume it primarily updates the direct orderLine if it's not a group,
    // or basic properties if it IS a group header.
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    const groupLine = updatedLines[categoryIndex]?.order_lines[groupLineIndex];

    if (!groupLine) return;

    if (!groupLine.isGroup) { // If it's a standard line, use individual product change
      handleIndividualProductChange(categoryIndex, groupLineIndex, field, value);
      return;
    }

    // For actual group headers (if applicable and structure matches PurchaseSection)
    groupLine[field] = value;
    if (field === 'quantity' || field === 'quantity_margin') {
      const quantity = parseFloat(groupLine.quantity) || 0;
      const margin = parseFloat(groupLine.quantity_margin) || 0;
      groupLine.quantity_total = quantity * (1 + margin / 100);
    }
    if (propagateToProducts && groupLine.products) {
      groupLine.products.forEach(product => {
        product[field] = value;
        if (field === 'quantity' || field === 'quantity_margin') {
          const quantity = parseFloat(product.quantity) || 0;
          const margin = parseFloat(product.quantity_margin) || 0;
          product.quantity_total = quantity * (1 + margin / 100);
        }
      });
    }
    setPurchaseOrderLines(updatedLines);
  };

  const handleNotesChange = (categoryIndex, orderLineIndex, notes, productIndex = null) => {
    if (!isPoEditable) return;
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    let targetLine;
    if (productIndex !== null) {
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex]?.products[productIndex];
    } else {
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex];
    }
    if (targetLine) {
      targetLine.note_client = notes.note_client;
      targetLine.note_supplier = notes.note_supplier;
    }
    setPurchaseOrderLines(updatedLines);
  };

  const handleFreeLineChange = (values) => {
    if (!isPoEditable) return;
    setFreeLineValues(values);
  };

  const handleFreeLineSubmit = (categoryIndex) => {
    if (!isPoEditable) return;
    if (!freeLineValues.free_product_name) {
      toast.error("Product name is required for a free line.");
      return;
    }
    // The handleAddProduct function (to be migrated) will take care of adding this to purchaseOrderLines
    // For now, we'll call a placeholder that would eventually use handleAddProduct
    // handleAddProduct("free_line", categoryIndex, freeLineValues); 
    console.log("Submitting free line for category", categoryIndex, freeLineValues);
    // Create the new free line
    const newFreeLine = {
      id: `temp-free-${Date.now()}`, // Temporary ID
      is_free_line: true,
      isFreeType: true,
      free_product_name: freeLineValues.free_product_name,
      free_product_brand: freeLineValues.free_product_brand,
      free_product_description: freeLineValues.free_product_description,
      free_price: parseFloat(freeLineValues.free_price) || 0,
      quantity: 1,
      quantity_margin: 0,
      quantity_total: 1,
      is_chosen_option: true, // Default for new lines
      vat_percentage: 0.21, // Default VAT
    };

    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    if (updatedLines[categoryIndex] && updatedLines[categoryIndex].order_lines) {
      updatedLines[categoryIndex].order_lines.push(newFreeLine);
    } else {
      // This case should ideally be handled by how categories are added/managed
      console.error("Category not found for adding free line");
      toast.error("Could not add free line: category not found.");
      return;
    }
    setPurchaseOrderLines(updatedLines);

    setFreeLineValues({ free_product_name: '', free_product_brand: '', free_product_description: '', free_price: '' });
  };

  const handleDeleteOrderLine = async (categoryIndex, orderLineIndex) => {
    if (!isPoEditable) return;
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    const category = updatedLines[categoryIndex];
    if (!category || !category.order_lines) return;

    const lineToDelete = category.order_lines[orderLineIndex];
    if (!lineToDelete) return;

    const canDelete = templateMode || !lineToDelete.is_standard_option; // Example rule from PurchaseSection

    if (!canDelete) {
      toast.error("Standard options can only be deleted in template mode");
      return;
    }

    // If the line has an ID, it means it exists on the server.
    // If no ID, it's a newly added line not yet saved, so just remove from local state.
    if (!lineToDelete.id) {
      category.order_lines.splice(orderLineIndex, 1);
      if (category.order_lines.length === 0 && updatedLines.length > 1) {
        // Optional: remove category if it becomes empty
      }
      setPurchaseOrderLines(updatedLines);
      return;
    }

    // For lines with an ID, call the backend DELETE endpoint
    try {
      const response = await httpClient.delete(
        import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}/lines/${lineToDelete.id}`
      );

      if (response.status === 200) {
        category.order_lines.splice(orderLineIndex, 1);
        setPurchaseOrderLines(updatedLines);
        toast.success("Item deleted successfully.");
      } else {
        toast.error(response.data?.message || "Failed to delete item.");
      }
    } catch (error) {
      console.error('Error deleting purchase order line:', error);
      toast.error(error.response?.data?.message || "An error occurred while deleting the item.");
    }
  };

  const confirmDeleteCategory = async (categoryIndex) => {
    if (!isPoEditable) return;

    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    const categoryToDelete = updatedLines[categoryIndex];
    if (!categoryToDelete) return;

    // Check if category has any saved lines (lines with IDs)
    const savedLines = categoryToDelete.order_lines.filter(line => line.id && !line.id.toString().startsWith('temp-'));

    if (savedLines.length === 0) {
      // Category has no saved lines, just remove from local state
      updatedLines.splice(categoryIndex, 1);
      setPurchaseOrderLines(updatedLines);
      toast.success("Category deleted successfully.");
      return;
    }

    // Category has saved lines, need to delete them from backend
    try {
      const response = await httpClient.delete(
        import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}/categories/${categoryToDelete.category_id}`
      );

      if (response.status === 200) {
        updatedLines.splice(categoryIndex, 1);
        setPurchaseOrderLines(updatedLines);
        toast.success("Category and all its items deleted successfully.");
      } else {
        toast.error(response.data?.message || "Failed to delete category.");
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.message || "An error occurred while deleting the category.");
    }
  };

  const handleDeleteCategory = (categoryIndex) => {
    if (!isPoEditable) return;

    const categoryToConfirm = purchaseOrderLines[categoryIndex];
    if (!categoryToConfirm) return;

    const hasSavedLines = categoryToConfirm.order_lines?.some(line => line.id && !line.id.toString().startsWith('temp-'));
    const message = hasSavedLines
      ? `Delete "${categoryToConfirm.category_name}" and all its saved purchase order lines? This cannot be undone.`
      : `Delete "${categoryToConfirm.category_name}" and all its purchase order lines?`;

    showNotificationPopup({
      message,
      onAction: () => confirmDeleteCategory(categoryIndex),
      actionButton: { text: "Delete", color: "var(--red)" }
    });
  };

  const handleUpdateProductCategory = (categoryIndex, selectedCategory) => {
    if (!isPoEditable) return;
    if (!selectedCategory || !selectedCategory.id || !selectedCategory.name) {
      toast.error("Invalid category selected.");
      return;
    }

    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    const categoryToUpdate = updatedLines[categoryIndex];
    if (!categoryToUpdate) return;

    if (categoryToUpdate.category_id === selectedCategory.id) {
      return;
    }

    const existingCategoryIndex = updatedLines.findIndex((category, index) => (
      index !== categoryIndex && category.category_id === selectedCategory.id
    ));

    if (existingCategoryIndex >= 0) {
      const movedLines = categoryToUpdate.order_lines || [];
      updatedLines[existingCategoryIndex].order_lines = [
        ...(updatedLines[existingCategoryIndex].order_lines || []),
        ...movedLines
      ];
      updatedLines.splice(categoryIndex, 1);
      toast.info("Product category merged with the existing category. Save the purchase order to persist this change.");
    } else {
      categoryToUpdate.category_id = selectedCategory.id;
      categoryToUpdate.category_name = selectedCategory.name;
      toast.info("Product category updated. Save the purchase order to persist this change.");
    }

    setPurchaseOrderLines(updatedLines);
  };

  // Placeholder for handleDeleteGroupProduct (if groups are deeply managed)
  // const handleDeleteGroupProduct = (categoryIndex, groupLineIndex, productIndex) => { ... };

  const fetchProductPrices = async (productId) => {
    try {
      // Endpoint from PurchaseSection, seems generic enough
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/product/get-prices?product_id=${productId}`);
      if (response.status === 200) {
        return response.data.map(price => ({
          id: price.id, // actual price record ID
          name: `
            <div class="price-option">
              <div class="price-option-header">
                <strong>${price.installer_id?.company_name || 'N/A'}</strong>
                <span class="price-value">€${parseFloat(price.price_brut_incl_installation || 0).toFixed(2)}</span>
              </div>
              <div class="price-option-details">
                <span class="supplier">Supplier: ${price.supplier_id?.company_name || 'N/A'} ${price.SKU_supplier ? `(SKU: ${price.SKU_supplier})` : ''}</span>
                <span class="date">${price.record_date ? formatDate(price.record_date) : 'No date'}</span>
              </div>
              ${price.price_net_incl_installation || price.price_brut_excl_installation || price.price_net_excl_installation ? `
              <div class="price-option-additional">
              ${price.price_net_incl_installation ? `<div>Net incl. installation: <strong>€${parseFloat(price.price_net_incl_installation).toFixed(2)}</strong></div>` : ''}
                ${price.price_brut_excl_installation ? `<div>Brut excl. installation: <strong>€${parseFloat(price.price_brut_excl_installation).toFixed(2)}</strong></div>` : ''}
                ${price.price_net_excl_installation ? `<div>Net excl. installation: <strong>€${parseFloat(price.price_net_excl_installation).toFixed(2)}</strong></div>` : ''}
              </div>
              ` : ''}
            </div>
          `,
          // Store the actual price object if needed for applying to the line
          price_details: price
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching product prices:', error);
      toast.error("Failed to fetch product prices.")
      throw error; // Re-throw for PopupDropdown to handle
    }
  };

  const handleSelectProductPrice = async (categoryIndex, orderLineIndex, productIndex = null) => {
    // This function is used by PopupDropdown's optionsPromise
    // It fetches prices and returns them in the format expected by PopupDropdown.
    let productId;
    const line = purchaseOrderLines[categoryIndex]?.order_lines[orderLineIndex];
    if (!line) return [];

    if (productIndex !== null && line.products) { // Product in a group
      productId = line.products[productIndex]?.product_id;
    } else { // Standard line item
      productId = line.product_id;
    }

    if (!productId) {
      toast.warning("Product ID not found for price fetching.");
      return [];
    }
    return fetchProductPrices(productId); // fetchProductPrices now returns formatted options
  };

  const handlePriceSelected = (categoryIndex, orderLineIndex, selectedPriceOption, productIndex = null) => {
    if (!isPoEditable) return;
    // selectedPriceOption is what PopupDropdown returns (the full object from formattedPrices)
    // null means clear the price
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    let targetLine;

    if (productIndex !== null) {
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex]?.products[productIndex];
    } else {
      targetLine = updatedLines[categoryIndex]?.order_lines[orderLineIndex];
    }

    if (targetLine) {
      if (selectedPriceOption === null) {
        // Clear the price
        targetLine.product_price_id = null;
        targetLine.price_details = null;
        setPurchaseOrderLines(updatedLines);
      } else if (selectedPriceOption && selectedPriceOption.price_details) {
        // Set the price
        targetLine.product_price_id = selectedPriceOption.id; // Store the price record ID
        targetLine.price_details = {
          id: selectedPriceOption.price_details.id,
          installer_company_name: selectedPriceOption.price_details.installer_id?.company_name || 'N/A',
          supplier_company_name: selectedPriceOption.price_details.supplier_id?.company_name || 'N/A',
          SKU_supplier: selectedPriceOption.price_details.SKU_supplier || '',
          price_brut_incl_installation: selectedPriceOption.price_details.price_brut_incl_installation,
          price_brut_excl_installation: selectedPriceOption.price_details.price_brut_excl_installation,
          price_net_incl_installation: selectedPriceOption.price_details.price_net_incl_installation,
          price_net_excl_installation: selectedPriceOption.price_details.price_net_excl_installation,
          record_date: selectedPriceOption.price_details.record_date
        };
        setPurchaseOrderLines(updatedLines);
      } else {
        toast.error("Failed to update price. Selected price data is missing.");
      }
    } else {
      toast.error("Failed to find target line for price update.");
    }
  };

  const toggleAlternativeButtons = (categoryIndex, orderLineIndex) => {
    if (showingAlternativeButtons &&
      showingAlternativeButtons.categoryIndex === categoryIndex &&
      showingAlternativeButtons.orderLineIndex === orderLineIndex) {
      setShowingAlternativeButtons(null);
    } else {
      setShowingAlternativeButtons({
        categoryIndex,
        orderLineIndex
      });
    }
  };

  const handleAddProductCategory = (category) => {
    if (!isPoEditable) return;
    // Adds a new category to the purchaseOrderLines state
    if (!category || !category.id || !category.name) {
      toast.error("Invalid category selected.");
      return;
    }
    const newCategory = {
      category_id: category.id,
      category_name: category.name,
      order_lines: [],
      // id: `temp-cat-${Date.now()}` // Optional: temp ID for UI key if needed before save
    };
    setPurchaseOrderLines([...purchaseOrderLines, newCategory]);
  };

  const handleAddProduct = (type, categoryIndex, valueObj, parentLineId = null) => {
    if (!isPoEditable) return;
    // type: "product", "group", "free_line", or "alternative_product", "alternative_group"
    const updatedLines = JSON.parse(JSON.stringify(purchaseOrderLines));
    let orderLineCategory = updatedLines[categoryIndex];

    if (!orderLineCategory) {
      toast.error("Category not found to add product to.");
      return;
    }
    if (!orderLineCategory.order_lines) {
      orderLineCategory.order_lines = []; // Initialize if undefined
    }

    let newOrderLine = {
      id: `temp-${type}-${Date.now()}`, // Temporary ID
      quantity: type === 'free_line' ? 1 : 0,
      quantity_margin: 0,
      quantity_total: type === 'free_line' ? 1 : 0,
      is_chosen_option: true, // Default for new lines
      vat_percentage: 0.21, // Default VAT
      // Common fields, specific fields added below
    };

    if (type === "product") {
      newOrderLine = {
        ...newOrderLine,
        product_id: valueObj.id,
        product_name: valueObj.product, // Assuming valueObj is a product object
        product_brand: valueObj.brand,
        product_internal_sku: valueObj.product_internal_sku,
        product_description: valueObj.product_description,
        is_standard_option: !parentLineId, // True if not an alternative
        standard_option_id: parentLineId || null,
        isAlternative: !!parentLineId,
      };
    } else if (type === "free_line") {
      newOrderLine = {
        ...newOrderLine,
        is_free_line: true,
        isFreeType: true,
        free_product_name: valueObj.free_product_name,
        free_product_brand: valueObj.free_product_brand,
        free_product_description: valueObj.free_product_description,
        free_price: parseFloat(valueObj.free_price) || 0,
        // quantity is already set to 1, quantity_total to 1
      };
      // Reset freeLineValues after adding
      setFreeLineValues({ free_product_name: '', free_product_brand: '', free_product_description: '', free_price: '' });
    } else if (type === "group") {
      // Simplified group handling for now, more complex structure from PurchaseSection can be added if needed
      newOrderLine = {
        ...newOrderLine,
        isGroup: true,
        group_id: valueObj.id, // Assuming valueObj is a product group object
        group_name: valueObj.group_name,
        isExpanded: true,
        products: valueObj.products ? valueObj.products.map(p => ({ // If group comes with products
          id: `temp-grp-prod-${Date.now()}`,
          product_id: p.id,
          product_name: p.product,
          product_brand: p.brand,
          product_internal_sku: p.product_internal_sku,
          product_description: p.product_description,
          quantity: 0, quantity_margin: 0, quantity_total: 0,
          is_chosen_option: true, vat_percentage: 0.21,
          parent_group_id: valueObj.id,
        })) : [],
        is_standard_option: !parentLineId,
        standard_option_id: parentLineId || null,
        isAlternative: !!parentLineId,
      };
    }
    // TODO: Handle alternative types by finding parent and inserting after it.
    // For now, alternatives will be added to the end of the category's lines.

    if (parentLineId) { // Adding as an alternative
      let parentLineIndex = orderLineCategory.order_lines.findIndex(line => line.id === parentLineId);
      if (parentLineIndex !== -1) {
        orderLineCategory.order_lines.splice(parentLineIndex + 1, 0, newOrderLine);
      } else {
        orderLineCategory.order_lines.push(newOrderLine); // Fallback
      }
      setShowingAlternativeButtons(null); // Close alternatives dropdown
    } else { // Adding a new standard item or free line
      orderLineCategory.order_lines.push(newOrderLine);
    }

    // The full sortOrderLinesByParentAlternative might be too complex if IDs are temporary.
    // A simpler sort or re-fetch after save might be better.
    // For now, just update the state.
    setPurchaseOrderLines(updatedLines);
  };

  const handleAddAlternativeProduct = (categoryIndex, orderLineIndex, product) => {
    if (!isPoEditable) return;
    const parentLine = purchaseOrderLines[categoryIndex]?.order_lines[orderLineIndex];
    if (!parentLine || !parentLine.id) {
      toast.error("Cannot add alternative: parent line not found or not saved.");
      return;
    }
    handleAddProduct("product", categoryIndex, product, parentLine.id);
  };

  const convertToTemplateFormat = (lines) => {
    return lines.map(category => {
      const filteredOrderLines = category.order_lines.filter(line => line.is_standard_option);
      return {
        category_id: category.category_id,
        category_name: category.category_name,
        order_lines: filteredOrderLines.map(line => {
          const templateLine = {
            product_id: line.product_id,
            product_name: line.product_name,
            product_brand: line.product_brand,
            quantity: line.quantity || 0,
            quantity_margin: line.quantity_margin || 0,
            quantity_total: line.quantity_total || 0,
            is_chosen_option: line.is_chosen_option || false,
            is_standard_option: line.is_standard_option || false,
            isAlternative: line.isAlternative || false,
            standard_option_id: line.standard_option_id || null,
            to_include_in_device_list: line.to_include_in_device_list || false,
            to_include_in_water_inspection_list: line.to_include_in_water_inspection_list || false,
            is_consumption_water: line.is_consumption_water || false,
            is_rainwater: line.is_rainwater || false,
            is_hidden_for_client: line.is_hidden_for_client || false,
            vat_percentage: line.vat_percentage || 0,
            discount_percentage: line.discount_percentage || 0,
            note_client: line.note_client || '',
            note_supplier: line.note_supplier || '',
            status: line.status || 'to_start',
            product_price_id: line.product_price_id || null
          };
          if (line.isGroup) {
            templateLine.isGroup = true;
            templateLine.group_id = line.group_id;
            templateLine.group_name = line.group_name;
            const filteredProducts = line.products?.filter(product => product.is_standard_option) || [];
            templateLine.products = filteredProducts.map(product => ({
              product_id: product.product_id,
              product_name: product.product_name,
              product_brand: product.product_brand,
              quantity: product.quantity || 0,
              quantity_margin: product.quantity_margin || 0,
              quantity_total: product.quantity_total || 0,
              is_chosen_option: product.is_chosen_option || false,
              is_standard_option: product.is_standard_option || false,
              isAlternative: product.isAlternative || false,
              standard_option_id: product.standard_option_id || null,
              to_include_in_device_list: product.to_include_in_device_list || false,
              to_include_in_water_inspection_list: product.to_include_in_water_inspection_list || false,
              is_consumption_water: product.is_consumption_water || false,
              is_rainwater: product.is_rainwater || false,
              is_hidden_for_client: product.is_hidden_for_client || false,
              vat_percentage: product.vat_percentage || 0,
              discount_percentage: product.discount_percentage || 0,
              note_client: product.note_client || '',
              note_supplier: product.note_supplier || '',
              status: product.status || 'to_start',
              product_price_id: product.product_price_id || null
            }));
          }
          return templateLine;
        })
      };
    }).filter(category => category.order_lines.length > 0);
  };

  const handleSaveTemplateSubmit = async (templateData) => {
    try {
      const existingTemplate = templates.find(t => t.name.toLowerCase() === templateData.name.toLowerCase());
      const formattedPurchaseOrderLines = convertToTemplateFormat(purchaseOrderLines);

      const requestData = {
        name: templateData.name,
        description: templateData.description,
        is_public: templateData.isPublic,
        purchaseOrderLines: formattedPurchaseOrderLines
      };

      if (existingTemplate) {
        requestData.template_id = existingTemplate.id; // For updating existing template
      }

      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/purchase-order-templates/save`,
        requestData
      );

      if (response.status === 200) {
        toast.success(existingTemplate ? "Template updated successfully" : "Template saved successfully");
        setShowSaveTemplateModal(false);
        await fetchTemplates(); // Refresh templates list
        return response;
      } else {
        toast.error(response.data?.message || "Failed to save template.");
        throw new Error(response.data?.message || "Failed to save template.")
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.message || error.message || "Failed to save template: Unknown error");
      throw error;
    }
  };

  const handleApplyTemplate = async (template) => {
    if (!poId || !template || !template.id) {
      toast.error("Cannot apply template: Missing PO ID or template information.");
      return;
    }
    try {
      // Ensure products are loaded before applying template
      if (!products || products.length === 0) {
        await fetchProducts();
      }

      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/purchase-order-templates/apply/${template.id}/to-po/${poId}`
      );

      if (response.status === 200) {
        await refetchPurchaseOrderData();
        setTemplateMode(false); // Turn off template mode since PO now has content
        toast.success(`Template "${template.name}" applied successfully to PO ${poId}.`);
      } else {
        toast.error(response.data?.message || "Failed to apply template.");
      }
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error(error.response?.data?.message || "Failed to apply template.");
    }
  };

  // Helper function to copy text to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Email address copied to clipboard!");
  };

  const invoiceTypeOptions = [
    { id: 'buyer_choices', name: 'Buyer Choices' },
    { id: 'other', name: 'Other' }
  ];

  const mapInvoicedToFromResponse = (items = []) => {
    return items.map((item) => ({
      type: item.type,
      contact: item.contact,
      company: item.company,
      percentage: item.percentage || 0,
      source: item.source
    }));
  };

  const getInvoicedToOptions = () => {
    const contactOptions = contacts.map(contact => ({ ...contact, type: 'contact' }));
    const companyOptions = companies.map(company => ({ ...company, type: 'company' }));
    return [...contactOptions, ...companyOptions];
  };

  const getInvoicedToDisplayName = (entry) => {
    if (entry.type === 'contact') {
      const contact = entry.contact || entry;
      return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact';
    }
    if (entry.type === 'company') {
      const company = entry.company || entry;
      return company.company_name || 'Unknown Company';
    }
    return 'Unknown';
  };

  const handleAddInvoiceRecipient = (option) => {
    if (!option) return;
    const alreadyAdded = invoicedTo.some(item => item.type === option.type &&
      (item.type === 'contact' ? item.contact?.id : item.company?.id) === option.id);
    if (alreadyAdded) return;
    setInvoicedTo(previous => [...previous, option.type === 'contact'
      ? { type: 'contact', contact: option, percentage: previous.length ? 0 : 100, source: 'manual' }
      : { type: 'company', company: option, percentage: previous.length ? 0 : 100, source: 'manual' }]);
  };

  const handleInvoicedToPercentageChange = (index, value) => {
    const parsed = parseFloat(value);
    const updated = invoicedTo.map((item, idx) => (
      idx === index ? { ...item, percentage: Number.isNaN(parsed) ? 0 : parsed } : item
    ));
    setInvoicedTo(updated);
  };

  const totalInvoicedPercentage = invoicedTo.reduce((sum, item) => sum + (parseFloat(item.percentage) || 0), 0);

  // Function to fetch purchase order data (reusable)
  const refetchPurchaseOrderData = async () => {
    setIsLoadingOrderLines(true);
    try {
      // Fetch the specific purchase order and supporting data in parallel
      const [poResponse] = await Promise.all([
        httpClient.get(import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}`),
        fetchProducts() // Ensure products are loaded before processing order lines
      ]);

      if (poResponse.data) {
        const poData = poResponse.data.purchaseOrder || poResponse.data;
        setPurchaseOrder(poData);
        setIsPoEditable((poData?.status || '').toLowerCase() === 'draft');
        setUnit(poResponse.data.unit || poData?.unit_id);
        setProject(poResponse.data.project || poResponse.data.unit?.building_id?.project_id || poData?.unit_id?.building_id?.project_id);
        setInvoiceType(poData?.invoice_type || 'buyer_choices');
        setApprovalDate(parseDateValue(poData?.approval_date));
        setInvoiceManualOverride(!!poData?.invoice_manual_override);
        setInvoicedTo(mapInvoicedToFromResponse(poData?.invoiced_to || []));
        setDefaultInvoicedTo(mapInvoicedToFromResponse(poData?.default_invoiced_to || []));

        // Process and set purchase order lines
        let processedLines = [];
        if (poResponse.data.purchaseOrderLines) {
          processedLines = processFetchedOrderLines(poResponse.data.purchaseOrderLines);
          setPurchaseOrderLines(processedLines);
        } else if (poResponse.data.purchaseOrder?.purchaseOrderLines) {
          processedLines = processFetchedOrderLines(poResponse.data.purchaseOrder.purchaseOrderLines);
          setPurchaseOrderLines(processedLines);
        }

        // Set template mode to true by default if PO is empty
        if (!processedLines || processedLines.length === 0) {
          setTemplateMode(true);
        }
      } else {
        throw new Error("Purchase order not found");
      }
    } catch (err) {
      console.error("Error fetching purchase order details:", err);
      setError(err.message || "Failed to fetch purchase order details.");
      toast.error(err.message || "Failed to fetch purchase order details.");
    } finally {
      setIsLoadingOrderLines(false);
    }
  };

  useEffect(() => {
    const fetchPurchaseOrderDetail = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await refetchPurchaseOrderData();
      } catch (err) {
        // Error handling is already done in refetchPurchaseOrderData
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSupportingData = async () => {
      // Fetch other data needed for editing (categories, groups, etc.)
      try {
        await Promise.all([
          fetchProductCategories(),
          fetchProductGroups(),
          fetchContacts(),
          fetchCompanies(),
          fetchTemplates()
        ]);
      } catch (error) {
        console.error('Error fetching supporting data:', error);
        toast.error('Failed to load supporting data for editing.');
      }
    };

    if (poId) {
      fetchPurchaseOrderDetail();
      fetchSupportingData();
    }

  }, [poId]);

  // useEffect to fetch POCs when unit data is available
  useEffect(() => {
    if (unit?.id) {
      fetchPocs(unit.id);
    }
  }, [unit]);

  const processFetchedOrderLines = (data) => {
    if (!data) return []; // Add guard for null or undefined data

    const responseData = data.map(category => ({
      ...category,
      order_lines: category.order_lines.map(line => {
        if (line.is_free_line) {
          return { ...line, isFreeType: true };
        }

        // Enrich line with product details if they're missing
        let enrichedLine = line;
        if (line.product_id && (!line.product_internal_sku || !line.product_description || !line.product_name || !line.product_brand)) {
          const product = products?.find(p => p.id === line.product_id);
          if (product) {
            enrichedLine = {
              ...line,
              product_name: line.product_name || product.product,
              product_brand: line.product_brand || product.brand,
              product_internal_sku: line.product_internal_sku || product.product_internal_sku,
              product_description: line.product_description || product.product_description
            };
          }
        }

        // Handle price details if they exist but are not in the expected UI format
        if (enrichedLine.product_price_id && !enrichedLine.price_details) {
          // Check for different possible field names that might contain price details
          const priceData = enrichedLine.product_price_details ||
            enrichedLine.product_price_id_details ||
            enrichedLine.price_data ||
            enrichedLine.priceDetails;

          if (priceData) {
            // Convert backend price details to UI format
            enrichedLine.price_details = {
              id: priceData.id,
              installer_company_name: priceData.installer_id?.company_name || 'N/A',
              supplier_company_name: priceData.supplier_id?.company_name || 'N/A',
              SKU_supplier: priceData.SKU_supplier || '',
              price_brut_incl_installation: priceData.price_brut_incl_installation,
              price_brut_excl_installation: priceData.price_brut_excl_installation,
              price_net_incl_installation: priceData.price_net_incl_installation,
              price_net_excl_installation: priceData.price_net_excl_installation,
              record_date: priceData.record_date
            };
          }
        }

        // Also enrich any nested group products
        if (enrichedLine.products && Array.isArray(enrichedLine.products)) {
          enrichedLine.products = enrichedLine.products.map(groupProduct => {
            let enrichedGroupProduct = groupProduct;

            if (groupProduct.product_id && (!groupProduct.product_internal_sku || !groupProduct.product_description || !groupProduct.product_name || !groupProduct.product_brand)) {
              const product = products?.find(p => p.id === groupProduct.product_id);
              if (product) {
                enrichedGroupProduct = {
                  ...groupProduct,
                  product_name: groupProduct.product_name || product.product,
                  product_brand: groupProduct.product_brand || product.brand,
                  product_internal_sku: groupProduct.product_internal_sku || product.product_internal_sku,
                  product_description: groupProduct.product_description || product.product_description
                };
              }
            }

            // Handle price details for group products too
            if (enrichedGroupProduct.product_price_id && !enrichedGroupProduct.price_details) {
              // Check for different possible field names that might contain price details
              const priceData = enrichedGroupProduct.product_price_details ||
                enrichedGroupProduct.product_price_id_details ||
                enrichedGroupProduct.price_data ||
                enrichedGroupProduct.priceDetails;

              if (priceData) {
                enrichedGroupProduct.price_details = {
                  id: priceData.id,
                  installer_company_name: priceData.installer_id?.company_name || 'N/A',
                  supplier_company_name: priceData.supplier_id?.company_name || 'N/A',
                  SKU_supplier: priceData.SKU_supplier || '',
                  price_brut_incl_installation: priceData.price_brut_incl_installation,
                  price_brut_excl_installation: priceData.price_brut_excl_installation,
                  price_net_incl_installation: priceData.price_net_incl_installation,
                  price_net_excl_installation: priceData.price_net_excl_installation,
                  record_date: priceData.record_date
                };
              }
            }

            return enrichedGroupProduct;
          });
        }

        return enrichedLine;
      })
    }));

    const sortedData = sortOrderLinesByParentAlternative(responseData);
    // Initialize all categories as collapsed by default
    const initialCollapsedState = {};
    sortedData.forEach((_, index) => {
      initialCollapsedState[index] = true;
    });
    setCollapsedCategories(initialCollapsedState);
    return sortedData;
  };

  // Sorting function
  const sortOrderLinesByParentAlternative = (purchaseOrderData) => {
    if (!purchaseOrderData) return [];
    const sortedData = JSON.parse(JSON.stringify(purchaseOrderData)); // Deep clone

    sortedData.forEach(category => {
      if (!category.order_lines || category.order_lines.length === 0) return;
      const standardOptions = category.order_lines.filter(line => !line.isAlternative && line.standard_option_id === null);
      const alternativesByParent = {};
      category.order_lines.forEach(line => {
        if (line.standard_option_id) {
          if (!alternativesByParent[line.standard_option_id]) {
            alternativesByParent[line.standard_option_id] = [];
          }
          alternativesByParent[line.standard_option_id].push(line);
        }
      });
      let sortedOrderLines = [];
      standardOptions.forEach(standardOption => {
        sortedOrderLines.push(standardOption);
        if (alternativesByParent[standardOption.id]) {
          sortedOrderLines = sortedOrderLines.concat(alternativesByParent[standardOption.id]);
        }
      });
      const processedIds = new Set(sortedOrderLines.map(line => line.id));
      const remainingItems = category.order_lines.filter(line => !processedIds.has(line.id));
      category.order_lines = sortedOrderLines.concat(remainingItems);
    });
    return sortedData;
  };

  // Data fetching functions
  const fetchProductCategories = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/product-categories/get`);
    if (response.status === 200) setProductCategoryList(response.data);
  };

  const fetchProducts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/products/get`);
    if (response.status === 200) {
      setProducts(response.data.map(product => ({
        ...product,
        name: `
          <div class="product-option">
            <div class="product-option-header">
              <strong>${product.product_internal_sku}</strong>
              <span class="product-brand">${product.brand}</span>
            </div>
            <div class="product-option-name">
              ${product.product}
            </div>
            ${product.product_description ? `
            <div class="product-option-description">
              ${product.product_description}
            </div>
            ` : ''}
          </div>
        `
      })));
    }
  };

  const fetchProductGroups = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/product/groups/get`);
    if (response.status === 200) setProductGroups(response.data);
  };

  const fetchContacts = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/contacts/get`);
      if (response.status === 200) setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get?type=limited-info`);
      if (response.status === 200) setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchTemplates = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/purchase-order-templates/get`);
    if (response.status === 200) setTemplates(response.data);
  };

  // --- POC Functions ---
  const fetchPocs = async (currentUnitId) => {
    if (!currentUnitId) return;
    setIsLoadingPocs(true);
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/units/pocs/get?unitID=${currentUnitId}`);
      if (response.status === 200) {
        setPocs(response.data);
      } else {
        toast.error("Failed to fetch POCs.");
      }
    } catch (error) {
      console.error("Error fetching POCs:", error);
      toast.error(error.response?.data?.message || "Failed to load POCs.");
    } finally {
      setIsLoadingPocs(false);
    }
  };

  const handleAddPoc = async (selectedPoc) => { // selectedPoc is the contact object from PopupDropdown
    if (!unit?.id || !selectedPoc?.id) {
      toast.error("Cannot add POC: Unit information or contact selection is missing.");
      return;
    }
    const data = {
      unitID: unit.id,
      contactID: selectedPoc.id
    };
    try {
      const response = await toast.promise(
        httpClient.post(import.meta.env.VITE_API_URL + `/api/units/pocs/create`, data),
        {
          success: `Person of contact has been added!`,
          error: "Something went wrong while adding POC.",
          pending: `Adding person of contact...`
        }
      );
      if (response.status === 200 || response.status === 201) { // Check for successful creation
        fetchPocs(unit.id); // Re-fetch POCs list
      }
    } catch (error) {
      // Toast promise already handles error display, but log for debugging
      console.error('Error adding POC:', error);
    }
  };

  const handleDeletePoc = async (pocToDelete) => {
    if (!unit?.id || !pocToDelete?.id) {
      toast.error("Cannot delete POC: Missing information.");
      return;
    }
    try {
      const response = await httpClient.delete(
        `${import.meta.env.VITE_API_URL}/api/units/pocs/delete?unitID=${unit.id}&contactID=${pocToDelete.id}`
      );
      if (response.status === 200) {
        toast.success("Contact removed successfully.");
        setPocs(pocs.filter(poc => poc.id !== pocToDelete.id));
      } else {
        toast.error(response.data?.message || "Failed to remove contact.");
      }
    } catch (error) {
      console.error('Error deleting POC:', error);
      toast.error(error.response?.data?.message || "An error occurred while removing the contact.");
    }
  };
  // --- End of POC Functions ---

  // Handle status change for purchase order
  const handleOnChangeStatus = (selectedStatus) => {
    if (!purchaseOrder || !selectedStatus) return;
    // Update local state only - will be saved when handleSavePurchaseOrder is called
    setPurchaseOrder(prev => ({
      ...prev,
      status: selectedStatus
    }));
    setIsPoEditable((selectedStatus || '').toLowerCase() === 'draft');
  };

  // Save Purchase Order functionality
  const handleSavePurchaseOrder = async () => {
    if (!purchaseOrder) return;
    try {
      // Sort the purchase order lines before saving
      const sortedPurchaseOrderLines = sortOrderLinesByParentAlternative(purchaseOrderLines);

      const response = await httpClient.put(
        import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}`,
        {
          // Include any editable PO fields including status
          ...purchaseOrder,
          invoice_type: invoiceType,
          approval_date: serializeDate(approvalDate),
          invoice_manual_override: invoiceManualOverride,
          invoiced_to: invoiceManualOverride ? invoicedTo : [],
          purchaseOrderLines: sortedPurchaseOrderLines
        }
      );

      if (response.status === 200 && response.data.purchaseOrderLines) {
        const processedLines = processFetchedOrderLines(response.data.purchaseOrderLines);
        setPurchaseOrderLines(processedLines);
        const nextStatus = (response.data?.purchaseOrder?.status || purchaseOrder?.status || '').toLowerCase();
        setIsPoEditable(nextStatus === 'draft');
        toast.success("Purchase Order saved successfully!");
      } else if (response.status === 200) {
        const nextStatus = (response.data?.purchaseOrder?.status || purchaseOrder?.status || '').toLowerCase();
        setIsPoEditable(nextStatus === 'draft');
        toast.success("Purchase Order saved successfully!");
      } else {
        toast.error(response.data?.message || "Failed to save Purchase Order.");
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error saving purchase order:", err);
      toast.error(err.response?.data?.message || "An error occurred while saving.");
      return false;
    }
  };

  // PDF generation
  const handleGeneratePDF = (type = 'client') => {
    if (!purchaseOrder) return;
    const url = `/purchase-orders/${poId}/pdf?type=${type}`;
    window.open(url, '_blank');
  };

  // Excel generation
  const handleGenerateExcel = async () => {
    if (!purchaseOrder) return;

    try {
      const url = import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}/excel`;
      const filename = `${unit?.unit_name || 'Unit'}_PO_${poId}_INSTALLER_.xlsx`;

      // Request Excel as a blob so we can detect JSON errors and show a toast instead of navigating
      const response = await httpClient.get(url, { responseType: 'blob' });
      const contentType = response.headers['content-type'];

      // If backend returned JSON instead of Excel, parse and show error toast
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          toast.error(errorData?.message || 'Failed to generate Excel file');
        } catch (e) {
          toast.error('Failed to generate Excel file');
        }
        return;
      }

      // Otherwise, treat it as a real Excel file and trigger download
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(error?.response?.data?.message || 'Failed to generate Excel file');
    }
  };

  const handleGenerateInvoice = async () => {
    if (purchaseOrder?.billing_status && purchaseOrder.billing_status !== 'not_invoiced') {
      navigate('/invoices?po=' + poId);
      return;
    }
    if (!purchaseOrder || purchaseOrder.status !== 'signed') {
      toast.error('Only signed purchase orders can be invoiced.');
      return;
    }
    setIsGeneratingInvoice(true);
    try {
      const saved = await handleSavePurchaseOrder();
      if (saved) navigate('/invoices?po=' + poId + '&draft=1');
    } finally { setIsGeneratingInvoice(false); }
  };

  if (isLoading) {
    return (
      <div className='template'>
        <Header />
        <MenuBar />
        <div className="template__body" id="body-purchase-order-detail">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='template'>
        <Header>
          <div className="back_button" onClick={() => navigate(-1)}><KeyboardBackspaceIcon /> Go Back</div>
        </Header>
        <MenuBar />
        <div className="template__body" id="body-po-detail-error">
          <p>Error loading purchase order: {error}</p>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className='template'>
        <Header>
          <div className="back_button" onClick={() => navigate(-1)}><KeyboardBackspaceIcon /> Go Back</div>
        </Header>
        <MenuBar />
        <div className="template__body" id="body-po-detail-error">
          <p>Purchase order not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='template'>
      <Header>
        <div className="header__left--po-detail">
          <div className="back_button" onClick={() => navigate(-1)}><KeyboardBackspaceIcon /> Go Back</div>
        </div>
        <div className="header__actions--po-detail">
          <div className='header__options--right'>
            <button className='primary-button-header' id="companies__btn-edit" onClick={handleSavePurchaseOrder}>SAVE</button>
          </div>
        </div>
      </Header>
      <MenuBar />
      <div className={`template__body ${showDetailInfo ? 'detail-expanded' : ''}`} id='body-purchase-order-detail'>
        <div className="body__section" id="po-order-lines">
          <div className='purchase-order-buttons'>
            <button
              className='icon-button green'
              title="Create client PDF"
              onClick={() => handleGeneratePDF('client')}
            >
              <AiOutlineFilePdf />
            </button>
            <button
              className='icon-button orange'
              title="Create supplier Excel"
              onClick={handleGenerateExcel}
            >
              <FaFileExcel />
            </button>
            <button
              className='icon-button purple'
              title="Preview draft invoice"
              aria-label="Preview draft invoice"
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || purchaseOrder?.status !== 'signed' || (purchaseOrder?.billing_status && purchaseOrder.billing_status !== 'not_invoiced')}
            >
              <MdAttachMoney />
            </button>
            <button
              className='icon-button red'
              title="Toggle template mode"
              onClick={() => setTemplateMode(!templateMode)}
              disabled={!isPoEditable}
            >
              <GrTemplate />
            </button>
            {templateMode && (
              <>
                <button
                  className='icon-button blue'
                  title="Save as template"
                  onClick={() => setShowSaveTemplateModal(true)}
                  disabled={!isPoEditable}
                >
                  <MdSave />
                </button>
                <PopupDropdown
                  icon={<MdOutlineCloudDownload />}
                  title="Apply template"
                  options={templates}
                  onChange={handleApplyTemplate}
                  searchBar={true}
                  padding="0px"
                  disabled={!isPoEditable}
                />
              </>
            )}
            <button
              className={`icon-button`}
              title="Toggle detail info"
              onClick={() => setShowDetailInfo(!showDetailInfo)}
            >
              <MdInfo />
            </button>
          </div>

          {!isPoEditable && (
            <div className="po-readonly-banner">
              This purchase order is not in draft status. Order lines are read-only, but you can still edit purchase order details.
            </div>
          )}

          {isLoadingOrderLines ? (
            <div className='loading__container'>
              <CircularProgress color="inherit" />
            </div>
          ) : (
            <div className='purchase_details--container'>
              <div className='product_categories'>
                <div className={`product_categories--order_line__header ${templateMode ? 'template-mode' : 'normal-mode'}`}>
                  <div className='product_categories--order_line__header_name'>Product</div>
                  <div className='product_categories--order_line__header_name'>Qty</div>
                  <div className='product_categories--order_line__header_name'>Margin</div>
                  <div className='product_categories--order_line__header_name'>Total Qty</div>
                  <div className='product_categories--order_line__header_name'>Chosen</div>
                  {!templateMode && (
                    <div className='product_categories--order_line__header_name'></div>
                  )}
                  {/* For Add Alternative button */}
                  <div className='product_categories--order_line__header_name'>Std. Opt.</div>
                  {templateMode && (
                    <>
                      {/* Template mode specific headers from PurchaseSection.jsx */}
                      <div className='product_categories--order_line__header_name'>Device List</div>
                      <div className='product_categories--order_line__header_name'>Water Inspection</div>
                      <div className='product_categories--order_line__header_name'>Consumption Water</div>
                      <div className='product_categories--order_line__header_name'>Rainwater</div>
                      <div className='product_categories--order_line__header_name'>Hidden</div>
                    </>
                  )}
                  <div className='product_categories--order_line__header_name'>VAT %</div>
                  <div className='product_categories--order_line__header_name'>Disc. %</div>
                  <div className='product_categories--order_line__header_name'>Notes</div>
                  <div className='product_categories--order_line__header_name'>Actions</div>
                </div>
                {purchaseOrderLines && purchaseOrderLines.length > 0 && (
                  <div className='product_categories--list'>
                    {purchaseOrderLines.map((category, categoryIndex) => (
                      <div className='product_category--item' key={category.id || categoryIndex}>
                        <div
                          className='product_category--title'
                        >
                          <div
                            className='category-title-content'
                            onClick={() => toggleCategoryCollapse(categoryIndex)}
                          >
                            <span>{categoryIndex + 1}. {category.category_name}</span>
                            {collapsedCategories[categoryIndex] ?
                              <KeyboardArrowRight style={{ marginLeft: '8px', fontSize: 20 }} /> :
                              <KeyboardArrowDown style={{ marginLeft: '8px', fontSize: 20 }} />
                            }
                            {templateMode && isPoEditable && (
                              <div className="product-category-actions" onClick={(e) => e.stopPropagation()}>
                                <PopupDropdown
                                  title="Update product category"
                                  options={productCategoryList}
                                  value={{
                                    id: category.category_id,
                                    name: category.category_name
                                  }}
                                  onChange={(selectedCategory) => handleUpdateProductCategory(categoryIndex, selectedCategory)}
                                  searchBar={true}
                                  customButton={(
                                    <button
                                      type="button"
                                      className='icon-button edit-category-button'
                                      title="Update product category"
                                    >
                                      <MdEdit />
                                    </button>
                                  )}
                                />
                                <button
                                  type="button"
                                  className='icon-button delete-category-button'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(categoryIndex);
                                  }}
                                  title="Delete category and all its items"
                                >
                                  <MdDelete />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        {!collapsedCategories[categoryIndex] && (
                          <>
                            <div className='product_category--order_lines'>
                              {category.order_lines.map((orderLine, orderLineIndex) => (
                                <React.Fragment key={orderLine.id || orderLineIndex}>
                                  {orderLine.is_free_line || orderLine.isFreeType ? (
                                    <FreeLineOrderLine
                                      orderLine={orderLine}
                                      categoryIndex={categoryIndex}
                                      orderLineIndex={orderLineIndex}
                                      templateMode={templateMode}
                                      isEditable={isPoEditable}
                                      handleIndividualProductChange={handleIndividualProductChange}
                                      handleGroupInputChange={handleGroupInputChange}
                                      handleNotesChange={handleNotesChange}
                                      handleDeleteOrderLine={handleDeleteOrderLine}
                                      convertVatToPercentage={convertVatToPercentage}
                                      convertPercentageToVat={convertPercentageToVat}
                                      convertDecimalToPercentage={convertDecimalToPercentage}
                                      convertPercentageToDecimal={convertPercentageToDecimal}
                                    />
                                  ) : (
                                    <StandardProductOrderLine
                                      orderLine={orderLine}
                                      categoryIndex={categoryIndex}
                                      orderLineIndex={orderLineIndex}
                                      templateMode={templateMode}
                                      isEditable={isPoEditable}
                                      handleIndividualProductChange={handleIndividualProductChange}
                                      handleGroupInputChange={handleGroupInputChange}
                                      handleNotesChange={handleNotesChange}
                                      handleDeleteOrderLine={handleDeleteOrderLine}
                                      handleSelectProductPrice={handleSelectProductPrice}
                                      handlePriceSelected={handlePriceSelected}
                                      toggleAlternativeButtons={toggleAlternativeButtons}
                                      convertVatToPercentage={convertVatToPercentage}
                                      convertPercentageToVat={convertPercentageToVat}
                                      convertDecimalToPercentage={convertDecimalToPercentage}
                                      convertPercentageToDecimal={convertPercentageToDecimal}
                                      hasAlternative={category.order_lines.some(l => l.standard_option_id === orderLine.id)}
                                      standardOption={category.order_lines.find(l => l.id === orderLine.standard_option_id)}
                                    />
                                  )}
                                  {/* Alternative options rendering (to be adapted from PurchaseSection) */}
                                  {showingAlternativeButtons &&
                                    showingAlternativeButtons.categoryIndex === categoryIndex &&
                                    showingAlternativeButtons.orderLineIndex === orderLineIndex && (
                                      <div className='alternative-options-container'>
                                        <div className='alternative-options'>
                                          <div className='alternative-option'>
                                            <PopupDropdown
                                              width="500px"
                                              title="Add Product Alternative"
                                              options={products} // Ensure products state is populated
                                              onChange={(product) => {
                                                handleAddAlternativeProduct(categoryIndex, orderLineIndex, product);
                                              }}
                                              searchBar={true}
                                              allowHTML={true}
                                              disabled={!isPoEditable}
                                            />
                                          </div>
                                          {/* Add Group Alternative if needed */}
                                        </div>
                                      </div>
                                    )}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className='product_category--buttons'>
                              {/* Add Product/Free Line buttons (to be adapted from PurchaseSection) */}
                              <div className={`po_add_button ${templateMode && isPoEditable ? "" : "hide"}`}>
                                <PopupDropdown
                                  width="500px"
                                  title="Add product"
                                  options={products} // Ensure products state is populated
                                  onChange={(product) => handleAddProduct("product", categoryIndex, product)}
                                  searchBar={true}
                                  allowHTML={true}
                                />
                              </div>
                              <div className={`po_add_button ${templateMode && isPoEditable ? "" : "hide"}`}>
                                <PopupInput
                                  title="Add free line"
                                  fields={[
                                    { id: 'free_product_name', label: 'Product Name', placeholder: 'Enter product name', type: "text" },
                                    { id: 'free_product_brand', label: 'Brand', placeholder: 'Enter brand name', type: "text" },
                                    { id: 'free_product_description', label: 'Description', placeholder: 'Enter product description', type: "textarea" },
                                    { id: 'free_price', label: 'Price', placeholder: 'Enter price (e.g. 199.99)', type: "number" }
                                  ]}
                                  values={freeLineValues} // Ensure freeLineValues state is managed
                                  onChange={handleFreeLineChange}
                                  onSave={() => handleFreeLineSubmit(categoryIndex)}
                                  width="450px"
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Show helpful message when PO is empty */}
                {(!purchaseOrderLines || purchaseOrderLines.length === 0) && templateMode && (
                  <div className="empty-po-message">
                    <p>This purchase order is empty. Start by adding a product category below.</p>
                  </div>
                )}
                {/* Always show "Add product category" button when in template mode */}
                <div className={`po_add_button ${templateMode && isPoEditable ? "" : "hide"}`}>
                  <PopupDropdown
                    title="Add product category"
                    options={productCategoryList} // Ensure productCategoryList state is populated
                    onChange={handleAddProductCategory}
                    searchBar={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`body__section ${showDetailInfo ? 'expanded' : 'collapsed'}`} id="po-detail-info">
          <div className="po-info-content">
            <div className="info-block">
              <h3 className='info-block__title'>Purchase Order</h3>
              <div className="info-details">
                <div className="info-item">
                  <span className="info-label">PO Number:</span>
                  <span className="info-value">{purchaseOrder?.id || poId}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Unit:</span>
                  <span className="info-value">{unit?.unit_name || 'Loading...'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Project:</span>
                  <span className="info-value">{project?.project_commercial_name || project?.project_placeholder_name || 'Loading...'}</span>
                </div>
                {purchaseOrder?.created_at && (
                  <div className="info-item">
                    <span className="info-label">Created:</span>
                    <span className="info-value">{new Date(purchaseOrder.created_at).toLocaleDateString()}</span>
                  </div>
                )}
                {purchaseOrder?.status && (
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <PopupDropdown
                      options={purchaseOrderStatusOptions}
                      value={purchaseOrder?.status}
                      onChange={handleOnChangeStatus}
                      title={"Select status"}
                      labelView={true}
                      labelColors={purchaseOrderStatusLabelColors}
                    />
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Type:</span>
                  <PopupDropdown
                    options={invoiceTypeOptions}
                    value={invoiceTypeOptions.find(option => option.id === invoiceType)}
                    onChange={(option) => setInvoiceType(option?.id)}
                    title={"Select type"}
                  />
                </div>
                <div className="info-item">
                  <span className="info-label">Billing:</span>
                  <span className="info-value">{({ not_invoiced: 'Not invoiced', reserved: 'Pending', draft: 'Draft created', invoiced: 'Invoiced' })[purchaseOrder.billing_status] || 'Not invoiced'}
                    {purchaseOrder?.billing_status && purchaseOrder.billing_status !== 'not_invoiced' && <button type="button" className="po-invoices-link" onClick={() => navigate('/invoices?po=' + poId)}>View invoices</button>}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Approval date:</span>
                  <DateField
                    className="date-picker po-approval-date"
                    format="DD-MM-YYYY"
                    value={approvalDate}
                    onChange={setApprovalDate}
                  />
                </div>
              </div>
            </div>

            <div className='invoiced-to-section'>
              <div className="invoiced-to-header">
                <h3>Invoiced to</h3>
                <label className="invoiced-to-toggle">
                  <input
                    type="checkbox"
                    checked={invoiceManualOverride}
                    onChange={(event) => {
                      setInvoiceManualOverride(event.target.checked);
                      if (!event.target.checked) setInvoicedTo(defaultInvoicedTo);
                    }}
                  />
                  Manual override
                </label>
              </div>
              {invoiceManualOverride && <div className="invoiced-to-add">
                <PopupDropdown
                  title="Add buyer"
                  options={getInvoicedToOptions().filter(option => !invoicedTo.some(item => item.type === option.type &&
                    (item.type === 'contact' ? item.contact?.id : item.company?.id) === option.id))}
                  onChange={handleAddInvoiceRecipient}
                  searchBar={true}
                  customButton={<button type="button" className="invoiced-to-icon-button" title="Add buyer" aria-label="Add buyer"><IoAddCircleOutline /></button>}
                />
              </div>}
              <div className="invoiced-to-list">
                {!invoicedTo.length && <span className="invoiced-to-empty">No buyers selected</span>}
                {invoicedTo.map((entry, index) => (
                  <div className="invoiced-to-item" key={`${entry.type}-${index}`}>
                    <span className="invoiced-to-name">{getInvoicedToDisplayName(entry)}</span>
                    {invoiceManualOverride ? <label className="invoiced-to-share"><input
                      className="invoiced-to-percentage"
                      type="number" aria-label={`${getInvoicedToDisplayName(entry)} percentage`}
                      min="0" max="100" step="0.01" value={entry.percentage}
                      onChange={(event) => handleInvoicedToPercentageChange(index, event.target.value)}
                    /><span>%</span></label> : <span className="invoiced-to-share-value">{entry.percentage}%</span>}
                    {invoiceManualOverride && <button type="button" className="invoiced-to-icon-button remove" title={`Remove ${getInvoicedToDisplayName(entry)}`}
                      aria-label={`Remove ${getInvoicedToDisplayName(entry)}`} onClick={() => setInvoicedTo(previous => previous.filter((_, itemIndex) => itemIndex !== index))}><MdDelete /></button>}
                  </div>
                ))}
              </div>
              <div className={`invoiced-to-total ${Math.round(totalInvoicedPercentage) !== 100 ? 'warning' : ''}`}>
                Total: {totalInvoicedPercentage.toFixed(2)}%
              </div>
            </div>

            <div className='pocs-section'>
              <div className="pocs-header">
                <h3>Contacts</h3>
                <div className='icon-button'>
                  <PopupDropdown
                    title="+"
                    options={contacts}
                    onChange={handleAddPoc}
                    searchBar={true}
                  />
                </div>
              </div>
              <div className='poc-list'>
                {isLoadingPocs ? (
                  <div className='loading__container'>
                    <CircularProgress color="inherit" />
                  </div>
                ) : pocs && pocs.length > 0 ? (
                  pocs.map((poc, index) => (
                    <div className='poc-item' key={poc.id || index}>
                      <div onClick={() => navigate(`/contacts/${poc.contact_id?.id || poc.id}`)}>
                        {poc.contact_id?.first_name || poc.first_name} {poc.contact_id?.last_name || poc.last_name}
                      </div>
                      <div
                        onClick={() => handleCopy(poc.contact_id?.email || poc.email)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <MdEmail /> {poc.contact_id?.email || poc.email}
                      </div>
                      <div
                        onClick={() => handleCopy(poc.contact_id?.mobile_phone || poc.mobile_phone)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <FaPhoneAlt /> {poc.contact_id?.mobile_phone || poc.mobile_phone}
                      </div>
                      <div className="poc-item__actions">
                        <button className="poc-delete-button" onClick={() => handleDeletePoc(poc)}>
                          <MdDelete />
                        </button>
                        <button className="poc-copy-button" onClick={() => handleCopy(poc.contact_id?.email || poc.email)}>
                          <IoCopySharp />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-pocs">No contacts assigned</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <TemplateModal
          isOpen={showSaveTemplateModal}
          onClose={() => setShowSaveTemplateModal(false)}
          onSave={handleSaveTemplateSubmit}
          initialIsPublic={true}
          templates={templates}
        />

      </div>
    </div>
  );
}

export default PurchaseOrderDetailPage;
